/* AnalyticsPage — teacher's Advanced Analytics dashboard.
 * KPI cards + AI insights + per-student risk table.
 * Feeds off /api/analytics/teacher.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Users, Clock, Trophy, ClipboardCheck, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import api from "@/lib/api";
import { usePager, Pager } from "@/components/ui/TablePager";

const KPI_META = [
  { key: "attendance_pct",       label: "Attendance",   icon: Clock,          tint: "#0EA5E9", suffix: "%" },
  { key: "quiz_avg",             label: "Quiz average", icon: Trophy,         tint: "#F59E0B", suffix: "%" },
  { key: "homework_completion",  label: "Homework",     icon: ClipboardCheck, tint: "#059669", suffix: "%" },
  { key: "skill_avg",            label: "Skill practice", icon: TrendingUp,   tint: "#7C3AED", suffix: "%" },
];

const RISK_META = {
  high:   { tint: "#DC2626", bg: "bg-red-50", ring: "border-red-200", label: "High risk" },
  medium: { tint: "#F59E0B", bg: "bg-amber-50", ring: "border-amber-200", label: "Watch" },
  low:    { tint: "#10B981", bg: "bg-emerald-50", ring: "border-emerald-200", label: "On track" },
};

const INSIGHT_META = {
  risk:        { icon: AlertTriangle, tint: "#DC2626", bg: "bg-red-50", ring: "border-red-200" },
  weakness:    { icon: TrendingDown,  tint: "#F59E0B", bg: "bg-amber-50", ring: "border-amber-200" },
  productivity:{ icon: Clock,         tint: "#F97316", bg: "bg-orange-50", ring: "border-orange-200" },
  positive:    { icon: CheckCircle2,  tint: "#10B981", bg: "bg-emerald-50", ring: "border-emerald-200" },
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/teacher");
        setData(data);
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to load analytics"); }
      finally { setLoading(false); }
    })();
  }, []);

  const studentPager = usePager((data && data.students) || [], 25);

  if (loading) return <div className="text-center text-neutral-500 py-16 text-sm">Loading analytics…</div>;
  if (!data)   return <div className="text-center text-neutral-500 py-16 text-sm">No data yet — analytics will populate as classes run.</div>;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ analytics</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter">Student insights</h1>
        <p className="text-sm text-neutral-600 mt-1">
          {data.totals.students} students · {data.totals.classes} classes · {data.totals.completed_classes} completed
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-kpis">
        {KPI_META.map((k, i) => {
          const Icon = k.icon;
          const val = data.kpis[k.key] ?? 0;
          const good = val >= 70;
          return (
            <motion.div key={k.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-black/8 rounded-xl p-4" data-testid={`kpi-${k.key}`}>
              <div className="flex items-center justify-between">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${k.tint}20`, color: k.tint }}>
                  <Icon size={14} />
                </div>
                <div className={`text-[10px] font-mono uppercase tracking-wider ${good ? "text-emerald-600" : "text-red-500"}`}>
                  {good ? "healthy" : "below target"}
                </div>
              </div>
              <div className="mt-3 text-3xl font-display font-bold tabular-nums">{val}{k.suffix}</div>
              <div className="mt-1 text-[11px] font-mono uppercase tracking-wider text-neutral-500">{k.label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* AI insights */}
      <div className="bg-white border border-black/8 rounded-xl p-5" data-testid="analytics-insights">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-[#0A0C10] text-[#FFD600] flex items-center justify-center">
            <Info size={12} />
          </div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ AI insights</div>
        </div>
        <div className="space-y-2">
          {data.insights.map((ins, i) => {
            const meta = INSIGHT_META[ins.kind] || INSIGHT_META.positive;
            const Icon = meta.icon;
            return (
              <div key={i} className={`flex items-start gap-2 rounded-md border p-3 ${meta.bg} ${meta.ring}`} data-testid={`insight-${i}`}>
                <Icon size={14} style={{ color: meta.tint }} className="mt-0.5 shrink-0" />
                <div className="text-sm text-[#0A0C10]">{ins.text}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-student table */}
      <div className="bg-white border border-black/8 rounded-xl overflow-hidden" data-testid="analytics-students">
        <div className="p-4 border-b border-black/8 flex items-center gap-2">
          <Users size={14} className="text-neutral-400" />
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ per student · sorted by engagement</div>
        </div>
        {data.students.length === 0 ? (
          <div className="text-center text-neutral-500 text-sm py-10">No students yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-[10px] font-mono uppercase tracking-wider text-neutral-500 text-left">
                <tr>
                  <th className="px-4 py-2">Student</th>
                  <th className="px-4 py-2 text-right">Engagement</th>
                  <th className="px-4 py-2 text-right">Attendance</th>
                  <th className="px-4 py-2 text-right">Quiz avg</th>
                  <th className="px-4 py-2 text-right">Homework</th>
                  <th className="px-4 py-2 text-right">Skills</th>
                  <th className="px-4 py-2 text-right">Risk</th>
                </tr>
              </thead>
              <tbody>
                {studentPager.pageItems.map((s) => {
                  const risk = RISK_META[s.risk] || RISK_META.low;
                  return (
                    <tr key={s.id} className="border-t border-black/5 hover:bg-neutral-50" data-testid={`student-row-${s.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-semibold">{s.name}</div>
                        <div className="text-[10px] font-mono text-neutral-400">{s.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                            <div className="h-full" style={{ width: `${s.engagement}%`, background: risk.tint }} />
                          </div>
                          <span className="tabular-nums font-semibold">{s.engagement}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{s.attendance_pct}%</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{s.quiz_avg}%</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{s.homework_pct}%</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{s.skill_pct}%</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${risk.bg}`} style={{ color: risk.tint }}>
                          {risk.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pager page={studentPager.page} pages={studentPager.pages} total={studentPager.total} onPage={studentPager.setPage} testid="analytics-pager" />
          </div>
        )}
      </div>

      {/* Totals footer */}
      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 flex flex-wrap gap-x-4 gap-y-1">
        <span>quizzes taken · {data.totals.quizzes_taken}</span>
        <span>hw assigned · {data.totals.homework_assigned}</span>
        <span>hw submitted · {data.totals.homework_submitted}</span>
        <span>skills · {data.totals.skills}</span>
        <span>skill attempts · {data.totals.skill_attempts}</span>
      </div>
    </div>
  );
}
